import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Moon, Palette, ShieldCheck } from "lucide-react";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { api, errMessage } from "../../lib/api";
import { CHILD_THEMES } from "../../lib/childThemes";

// A live mini mockup of the child area rendered with the REAL theme tokens —
// what you see here is exactly what the child gets. The three chips are
// interactive audio previews: tapping one has Pip speak the label in the
// family's selected coach voice. Pass big for the full-screen try-on view.
const speakLabel = async (e, label) => {
  e.stopPropagation(); // don't trigger the card's theme toggle
  try {
    const { data } = await api.post("/speech/theme-label-preview",
      { label }, { responseType: "blob" });
    new Audio(URL.createObjectURL(data)).play();
  } catch {
    toast.error("Could not play the preview right now");
  }
};

const chipCls = (big) =>
  `rounded-full font-bold cursor-pointer transition-transform hover:scale-105 ${
    big ? "text-base px-5 py-2.5" : "text-[9px] px-2 py-1"}`;

const ThemeMockup = ({ theme, big = false }) => (
  <div className={`theme-child ${theme.className} rounded-xl border border-border overflow-hidden select-none`}
    data-testid={`theme-mockup-${theme.key}${big ? "-big" : ""}`}>
    <div className={`bg-background ${big ? "p-8 space-y-5" : "p-3 space-y-2"}`}>
      <p className={`font-extrabold font-display text-foreground ${big ? "text-2xl" : "text-[11px]"}`}>Hi Mia! Ready to play?</p>
      <div className={`flex flex-wrap ${big ? "gap-3" : "gap-1.5"}`}>
        <button type="button" onClick={(e) => speakLabel(e, "Let's play")}
          className={`bg-primary text-primary-foreground ${chipCls(big)}`}
          data-testid={`theme-chip-play-${theme.key}${big ? "-big" : ""}`}
          aria-label="Hear Pip say Let's play in this coach voice">Let's play</button>
        <button type="button" onClick={(e) => speakLabel(e, "Story time")}
          className={`bg-secondary text-secondary-foreground ${chipCls(big)}`}
          data-testid={`theme-chip-story-${theme.key}${big ? "-big" : ""}`}
          aria-label="Hear Pip say Story time in this coach voice">Story time</button>
        <button type="button" onClick={(e) => speakLabel(e, "Stickers")}
          className={`bg-accent text-accent-foreground ${chipCls(big)}`}
          data-testid={`theme-chip-stickers-${theme.key}${big ? "-big" : ""}`}
          aria-label="Hear Pip say Stickers in this coach voice">Stickers</button>
      </div>
      <div className={`bg-card rounded-lg border border-border ${big ? "px-5 py-4" : "px-2 py-1.5"}`}>
        <p className={`font-bold text-card-foreground ${big ? "text-lg" : "text-[9px]"}`}>Sound safari</p>
        <p className={`text-muted-foreground ${big ? "text-sm" : "text-[8px]"}`}>Find the animal sounds</p>
      </div>
      {big && (
        <div className="bg-card rounded-lg border border-border px-5 py-4">
          <p className="text-lg font-bold text-card-foreground">Story time</p>
          <p className="text-sm text-muted-foreground">The brave little fox — chapter two</p>
        </div>
      )}
    </div>
  </div>
);

// Friendly clock label for the schedule selects: 19 -> "7 pm".
const hourLabel = (h) => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? "am" : "pm"}`;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const monthLabel = (ym) =>
  new Date(`${ym}-15`).toLocaleDateString(undefined, { month: "short" });

// Parents approve which palettes the child can pick from. Colors and
// animations only — permissions, consents and safety settings never change.
export const ThemeSettings = () => {
  const [enabled, setEnabled] = useState(null);
  const [comfort, setComfort] = useState(false);
  const [seasonal, setSeasonal] = useState({});
  const [schedule, setSchedule] = useState({ enabled: false, start: 19, end: 7 });
  const [history, setHistory] = useState([]);
  const [tryOn, setTryOn] = useState(null);

  useEffect(() => {
    api.get("/theme-settings")
      .then(({ data }) => {
        setEnabled(data.enabled_themes);
        setComfort(!!data.reading_comfort);
        setSeasonal(data.seasonal_active || {});
        setSchedule(data.dark_schedule || { enabled: false, start: 19, end: 7 });
      })
      .catch(() => setEnabled(null));
    api.get("/theme-history")
      .then(({ data }) => setHistory(data.children || []))
      .catch(() => setHistory([]));
  }, []);

  const save = async (nextEnabled, nextComfort, okMsg, nextSchedule = null) => {
    const prev = { enabled, comfort, schedule };
    setEnabled(nextEnabled);
    setComfort(nextComfort);
    if (nextSchedule) setSchedule(nextSchedule);
    try {
      const { data } = await api.put("/theme-settings",
        { enabled_themes: nextEnabled, reading_comfort: nextComfort,
          ...(nextSchedule ? { dark_schedule: nextSchedule } : {}) });
      setEnabled(data.enabled_themes);
      setComfort(!!data.reading_comfort);
      setSchedule(data.dark_schedule);
      toast.success(okMsg);
    } catch (e) {
      setEnabled(prev.enabled);
      setComfort(prev.comfort);
      setSchedule(prev.schedule);
      toast.error(errMessage(e));
    }
  };

  const toggle = (key) => {
    const next = enabled.includes(key)
      ? enabled.filter((t) => t !== key)
      : [...enabled, key];
    if (!next.length) {
      toast.info("Keep at least one theme available for the child area");
      return;
    }
    save(next, comfort, "Child themes updated");
  };

  if (!enabled) return null;
  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-4" data-testid="theme-settings">
      <div>
        <p className="text-sm font-bold flex items-center gap-2">
          <Palette className="h-4 w-4 text-secondary" aria-hidden="true" /> Child area themes
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Choose which looks your child can pick from in their play space. Each preview below
          is a live mockup in that exact color scheme.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {CHILD_THEMES.map((t) => {
          const outOfSeason = t.seasonal && !seasonal[t.key];
          return (
            <div key={t.key} className={`rounded-2xl border p-3 space-y-2 transition-colors ${
              enabled.includes(t.key) ? "border-secondary/50 bg-secondary/5" : "border-border opacity-80"}`}
              data-testid={`theme-card-${t.key}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-bold flex items-center gap-1.5">
                  {t.name}
                  {t.seasonal && (
                    <span className="rounded-full bg-red-100 border border-red-300 text-red-800 text-[9px] font-bold px-1.5 py-0.5"
                      data-testid="theme-seasonal-badge">
                      {outOfSeason ? `Returns in ${t.returnsIn}` : "Limited time"}
                    </span>
                  )}
                </p>
                <Switch checked={enabled.includes(t.key)} disabled={outOfSeason}
                  onCheckedChange={() => toggle(t.key)}
                  aria-label={`Enable ${t.name} for the child area`}
                  data-testid={`theme-toggle-${t.key}`} />
              </div>
              {/* Non-interactive wrapper (mockup contains its own chip buttons —
                  nesting them in a button is invalid HTML). Mouse users can
                  still click the mockup; keyboard users get the button below. */}
              <div onClick={() => setTryOn(t)}
                className="rounded-xl transition-transform hover:scale-[1.02] cursor-pointer"
                data-testid={`theme-tryon-${t.key}`}>
                <ThemeMockup theme={t} />
              </div>
              <button type="button" onClick={() => setTryOn(t)}
                className="text-[11px] font-semibold text-secondary hover:underline underline-offset-2"
                aria-label={`Preview ${t.name} full screen`}
                data-testid={`theme-tryon-open-${t.key}`}>
                Full-screen preview
              </button>
              <p className="text-[11px] text-muted-foreground">
                {outOfSeason ? `This seasonal look comes back every ${t.returnsIn} and tucks itself away after.` : t.description}
              </p>
              {t.key === "dark" && (
                <div className="border-t border-border pt-2 space-y-2" data-testid="theme-dark-schedule">
                  <label className="flex items-center justify-between gap-2 text-[11px] font-semibold cursor-pointer">
                    <span className="flex items-center gap-1.5">
                      <Moon className="h-3.5 w-3.5 text-secondary" aria-hidden="true" />
                      Switch on automatically in the evening
                    </span>
                    <Switch checked={schedule.enabled} disabled={!enabled.includes("dark")}
                      onCheckedChange={(v) => save(enabled, comfort,
                        v ? "Evening dark mode on" : "Evening dark mode off",
                        { ...schedule, enabled: Boolean(v) })}
                      aria-label="Evening dark mode schedule"
                      data-testid="theme-dark-schedule-toggle" />
                  </label>
                  {schedule.enabled && (
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold" data-testid="theme-dark-schedule-hours">
                      From
                      <select value={schedule.start}
                        onChange={(e) => save(enabled, comfort, "Evening window updated",
                          { ...schedule, start: Number(e.target.value) })}
                        className="rounded-md border border-border bg-background px-1.5 py-1 text-[11px]"
                        aria-label="Dark mode start hour" data-testid="theme-dark-start">
                        {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                      </select>
                      to
                      <select value={schedule.end}
                        onChange={(e) => save(enabled, comfort, "Evening window updated",
                          { ...schedule, end: Number(e.target.value) })}
                        className="rounded-md border border-border bg-background px-1.5 py-1 text-[11px]"
                        aria-label="Dark mode end hour" data-testid="theme-dark-end">
                        {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                      </select>
                    </div>
                  )}
                  <button type="button" onClick={() => setTryOn(t)}
                    className="text-[11px] font-semibold text-secondary hover:underline underline-offset-2"
                    aria-label="Preview the bedtime dark look full screen"
                    data-testid="theme-bedtime-preview">
                    Preview the bedtime look
                  </button>
                </div>
              )}
              {t.key === "contrast" && (
                <label className="flex items-center justify-between gap-2 text-[11px] font-semibold cursor-pointer border-t border-border pt-2">
                  Extra letter room for early readers
                  <Switch checked={comfort}
                    onCheckedChange={(v) => save(enabled, Boolean(v),
                      v ? "Extra letter room on" : "Extra letter room off")}
                    aria-label="Extra letter spacing for High Contrast"
                    data-testid="theme-reading-comfort" />
                </label>
              )}
            </div>
          );
        })}
      </div>
      {history.length > 0 && (
        <div className="border-t border-border pt-3 space-y-2" data-testid="palette-history">
          <p className="text-xs font-bold flex items-center gap-1.5">
            <History className="h-3.5 w-3.5 text-secondary" aria-hidden="true" /> Color history
          </p>
          <p className="text-[11px] text-muted-foreground">
            Each child's favorite look, month by month — a tiny timeline of changing tastes.
          </p>
          <div className="space-y-1.5">
            {history.map((kid) => (
              <div key={kid.child_id} className="flex items-center gap-2 flex-wrap"
                data-testid={`palette-history-${kid.child_id}`}>
                <span className="text-[11px] font-bold w-16 truncate">{kid.display_name}</span>
                {kid.months.map((m) => {
                  const t = CHILD_THEMES.find((x) => x.key === m.theme);
                  if (!t) return null;
                  return (
                    <span key={m.month} title={`${monthLabel(m.month)} — ${t.name}`}
                      className="inline-flex flex-col items-center gap-0.5"
                      data-testid={`palette-history-chip-${kid.child_id}-${m.month}`}>
                      <span className="h-5 w-8 rounded-md border border-black/10 overflow-hidden flex">
                        <span className="flex-1" style={{ backgroundColor: t.swatch[1] }} />
                        <span className="flex-1" style={{ backgroundColor: t.swatch[2] }} />
                      </span>
                      <span className="text-[9px] font-semibold text-muted-foreground">{monthLabel(m.month)}</span>
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="theme-safety-note">
        <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-secondary" aria-hidden="true" />
        Themes only change colors and animations — permissions, safety settings and layout never change.
      </p>

      <Dialog open={!!tryOn} onOpenChange={(o) => !o && setTryOn(null)}>
        <DialogContent className="max-w-2xl" data-testid="theme-tryon-dialog">
          {tryOn && (
            <div className="space-y-4">
              <DialogTitle className="flex items-center gap-2">
                {tryOn.name} — full-screen preview
                {tryOn.seasonal && !seasonal[tryOn.key] && (
                  <span className="rounded-full bg-red-100 border border-red-300 text-red-800 text-[10px] font-bold px-2 py-0.5">
                    Returns in {tryOn.returnsIn}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription>
                Exactly how the child area looks in this color scheme — colors and animations only.
              </DialogDescription>
              <ThemeMockup theme={tryOn} big />
              <div className="flex justify-end">
                {enabled.includes(tryOn.key) ? (
                  <p className="text-xs font-semibold text-emerald-700" data-testid="tryon-enabled-note">
                    Already available to your child
                  </p>
                ) : (
                  <button type="button" disabled={tryOn.seasonal && !seasonal[tryOn.key]}
                    onClick={() => { toggle(tryOn.key); setTryOn(null); }}
                    className="rounded-full bg-secondary text-secondary-foreground text-xs font-bold px-4 py-2 transition-transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                    data-testid="tryon-enable-btn">
                    Enable for my child
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
